import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { router } from "@/app/router";
import { ApiError, setSessionExpiredHandler } from "@/shared/api";
import { Toaster, TooltipProvider } from "@/shared/ui";
import { applyTheme, useTheme } from "./theme";

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // Retry only network errors and 5xx, never 4xx.
        retry: (count, error) => count < 2 && (!(error instanceof ApiError) || error.status >= 500),
      },
    },
  });
}

/** When a 401 cannot be recovered: drop every cached byte of the old session and go to /login. */
function SessionExpiryHandler({ client }: { client: QueryClient }) {
  useEffect(() => {
    setSessionExpiredHandler(() => {
      client.clear();
      const { pathname, search } = router.state.location;
      if (pathname === "/login") return;
      void router.navigate(`/login?next=${encodeURIComponent(pathname + search)}`, {
        replace: true,
      });
    });
    return () => setSessionExpiredHandler(null);
  }, [client]);
  return null;
}

export function Providers({ children }: { children: ReactNode }) {
  const [client] = useState(makeQueryClient);
  const theme = useTheme();
  useEffect(() => applyTheme(theme), [theme]);
  return (
    <QueryClientProvider client={client}>
      <SessionExpiryHandler client={client} />
      <TooltipProvider>
        {children}
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
